/**
 * This module keeps track of the current browser tab for the application
 * KAI-13341.
 * 
 * We are using local storage and window name to detect which is the current
 * browser tab. We set the window name to a unique value, and we compare it
 * with the activeTab from local storage. Initialy we used session storage 
 * instead of the window name, but this is not unique when a tab is 
 * duplicated (see: https://stackoverflow.com/a/45094537/1401409) 
 * 
 * Note: FF 71 onward has a bug that prevents sharing of localStorage in a
 *       consistent way. https://bugzilla.mozilla.org/show_bug.cgi?id=1453699
 */

//---- components ---------
//---- mixins -------------
//---- services -----------
//---- other --------------
import { appLocalStorage, storage } from './app-storage.js'
import routes from '../routes.js';
import store from '@/app/store/index.js'
//---- 3rd-parties --------

// shared info between all tabs - id of the currently active tab
const _activeTab = {
    storage: appLocalStorage,
    type: 'string',
    key: "activeTab"
}

// shared info between all tabs - id of the next tab
const _nextTab = {
    storage: appLocalStorage,
    type: 'int',
    key: "nextTab"
}

// shared info between all tabs - last time the check was done
const _lastActiveTabCheck = {
    storage: appLocalStorage,
    type: 'long',
    key: "lastActiveTabCheck"
}

// shared info between all tabs - the time at which the session will be timed out 
//                                if no other action is performed.
const _timeoutTime = {
    storage: appLocalStorage,
    type: 'long',
    key: "timeout"
}

// shared info between all tabs - the session timeout period
const _sessionTimeout = {
    storage: appLocalStorage,
    type: 'long',
    key: "sessionTimeout"
}

class BrowserTabService {
    
    debug = false

    //---------------------------------------------------------

    // call this method to know if this window (tab) is allowed.
    // if so return true to skip the active window check
    areActionsAllowed() {
        return this.isPopupWindow() || routes.currentRoute.name == null || routes.currentRoute.name == "kmofflineRegister";
    }

    isSecondTabAllowed() {
        return this.isPopupWindow() || routes.currentRoute.name == null || routes.currentRoute.name == "kmofflineRegister";
    }

    // call this method to know if this window (tab) is allowed.
    // if so return true to skip the active window check
    isPopupWindow() {
        return (""+window.name).startsWith("popup");
    }

    // called by a timer every 2 seconds
    // check if we are the active tab or not, update the vuex if needed
    // also update the last check time if we're the current active tab
    checkActiveWindow() {            
        // skip check for certain windows
        if (this.isSecondTabAllowed()) {
            if (this.debug)
                console.debug("Skipping activeTab check for popup tab/window")
            store.dispatch("header/setIsActiveTab", true)
            return;
        }
        if (this.debug)
            console.debug("Checking for active window")

        // make sure the nextTab has a correct value
        // this is to make sure that if we lost the nextTab value that we 
        // don't end up with 2 different tab 0 
        const nextTab = storage.get(_nextTab, 0);
        const currentTab = this.getCurrentTab();
        if (currentTab > nextTab && currentTab - nextTab < 2000)
            storage.set(_nextTab, currentTab + 1)

        // check if we're active tab and update it in vuex
        const isActiveTab = this.isActiveTab()

        // if we're the active tab, update the last check time
        if (isActiveTab && this.hasActiveTab())
            storage.set(_lastActiveTabCheck, (new Date()).getTime())
    }

    //----------------------------------------------------------------

    isActiveTabStale() {
        const lastCheck = storage.get(_lastActiveTabCheck, 0)
        const currentTimeMillis = (new Date()).getTime()
        return !lastCheck || (currentTimeMillis - lastCheck) > 3000
    }

    // return whether there currently is a tab where the user is logged in and
    // that is active
    hasActiveTab() {

        if (!this.isSessionInProgress())
            return false;

        const activeTab = storage.get(_activeTab, -1)

        // if there is no active tab yet, or if the active tab did not
        // report in the last 3 seconds, then we are on the active tab
        let hasActiveTab = !(activeTab < 0);

        if (hasActiveTab) {
            hasActiveTab &= !this.isActiveTabStale()
        }

        if (this.debug)
            console.log(hasActiveTab ? "hasActiveTab" : "no active tab activeTab: " + activeTab)

        return hasActiveTab
    }

    getActiveTab() {
        if (this.hasActiveTab()) {
            const activeTab = storage.get(_activeTab, null);
            return activeTab;
        }
        return null;
    }

    // returns whether a current session is in progress according to the global
    // UI state (local storage). It's possible that the backend session was lost
    // because of a restart or early timeout and that the UI (this method) 
    // doesn't know it yet
    isSessionInProgress() {
        const timeoutTime = storage.get(_timeoutTime, 0);
        const currentTimeMillis = (new Date()).getTime()
        const sessionInProgress = (currentTimeMillis < timeoutTime)
        if (this.debug)
            console.log(sessionInProgress ? "Session in progress" : "No Session")
        return sessionInProgress
    }

    //-------------------------------------------------------------------------

    // returns whether the current tab is logged-in (note that this doesn't mean 
    // there is an active session to back this. The session could have been 
    // logged out by another tab or just timed out)
    isCurrentTabLoggedIn() {
        // return logged in status of the tab according to vuex
        return store.getters['auth/loggedIn']
    }

    // returns whether the current tab is the active tab or not and update the 
    // vuex state to reflect the returned value
    isActiveTab() {

        if (this.isSecondTabAllowed()) {
            if (this.debug)
                console.debug("Skipping activeTab check for popup tab/window")
            store.dispatch("header/setIsActiveTab", true)
            return true;
        }

        let isActiveTab;
        if (this.isSessionInProgress()) {
            const currentTab = this.getCurrentTab()
            const activeTab = this.getActiveTab()
            const staleActiveTab = this.isActiveTabStale();
            if (this.debug)
                console.log("ActiveTab: " + activeTab + (staleActiveTab ? " (stale)" : "") + "  currentTab: " + currentTab)
            isActiveTab = activeTab === null || currentTab == activeTab || staleActiveTab
        }
        // if no session is open, then all tabs are active
        else {
            isActiveTab = true
        }

        if (this.debug)
            console.log(isActiveTab ? "Is Active Tab" : "Inactive")

        // update the vuex state if needed
        if (store.getters["header/isActiveTab"] != isActiveTab)
            store.dispatch("header/setIsActiveTab", isActiveTab)

        return isActiveTab
    }

    //-------------------------------------------------------------------------


    getCurrentTab() {
        let currentTab = window.name
        // in FF, the window.name is the string "null" in in-private mode
        if (!currentTab || currentTab == "null") {
            // generate a new tab id
            currentTab = this.getNextTab()
            if (this.debug)
                console.log("Setting windown.name to '" + currentTab + "'")
            window.name = currentTab
            // register event to detect when the page goes away
            // we execute the same action as onLogout, removing 
            // current tab as active tab if needed
            window.addEventListener('beforeunload', this.onLogout)
        }

        if (this.isSessionInProgress() && !this.hasActiveTab()) {
            if (this.debug)
                console.log("Taking ownership of active tab=" + currentTab)
            storage.set(_activeTab, currentTab)
            storage.set(_lastActiveTabCheck, (new Date()).getTime())
        }

        return currentTab
    }

    getNextTab() {
        let nextTab = parseInt(storage.get(_nextTab, 0))
        if (nextTab > 9999) // wrap around when the id is large, we should not have collisions
            nextTab = 0
        const currentTab = nextTab
        storage.set(_nextTab, nextTab + 1)
        return currentTab
    }

    makeCurrentTabActive() {
        storage.set(_activeTab, window.name)
        return true
    }

    onLogout() {
        window.removeEventListener('beforeunload', this.onLogout)
        // regardless of the active window, any window can clear storage.
        // note that this method is not only called on logout but also
        // when the session is passed the timeout. In that case it is 
        // important for other tabs to be able to clear the storage values
        // because the original active tab might not be around anymore.
        // if (window.name !== storage.get(_activeTab, -1))
        //     return
        if (this.debug)
            console.log("remove sessionTimeout")
        storage.clear(_timeoutTime)
        storage.clear(_sessionTimeout)
        storage.clear(_activeTab)
        // clearing the next tab causes issues because it may cause tab id collisions to occur
        // instead we will wrap around when the _nextTab has a high enough value that we are
        // certain the old tabs with low ids are not around anymore
        // storage.clear(_nextTab) 
    }

}

export default new BrowserTabService()